/**
 * PuntoControlRepository - Repositorio unificado de Puntos de Control
 *
 * Implementación compartida entre web (Dexie) y mobile (SQLite).
 *
 * Mismo modelo que ElementoMapaRepository: delete() es SOFT-delete (marca
 * is_deleted=true y emite el UPDATE de sync) porque `puntos_control` tiene
 * la columna is_deleted en Supabase, y así los borrados se propagan offline
 * vía UPDATE.
 */

import { v4 as uuidv4 } from 'uuid';
import {
  IPuntoControlRepository,
  PuntoControl
} from '../interfaces/IPuntoControlRepository';
import { BaseRepository } from './BaseRepository';
import { IDatabaseAdapter, WhereClause } from '../adapters/IDatabaseAdapter';
import { IProfileRepository } from '../interfaces/IProfileRepository';

export class PuntoControlRepository
  extends BaseRepository<PuntoControl, string>
  implements IPuntoControlRepository
{
  /** Clause reutilizable para excluir soft-deletes. */
  private readonly NOT_DELETED: WhereClause = { field: 'is_deleted', operator: 'equals', value: false };

  constructor(db: IDatabaseAdapter, profileRepo?: IProfileRepository) {
    super(db, 'puntos_control', 'puntos_control');

    this.supportsSoftDelete = true;
    this.autoTimestamp = true;

    if (profileRepo) {
      this.setProfileRepository(profileRepo);
    }
  }

  async add(data: Omit<PuntoControl, 'id'>): Promise<string> {
    const id = uuidv4();
    const now = this.getCurrentTimestamp();
    const profile = await this.getCurrentProfile();

    const record: PuntoControl = {
      ...data,
      id,
      creado_por: data.creado_por || profile?.user_id || '',
      empresa_id: data.empresa_id || profile?.empresa_activa_id || '',
      created_at: data.created_at || now,
      updated_at: now,
      is_deleted: false
    };

    await this.db.insert('puntos_control', record);
    this.emitSync('INSERT', record);
    return id;
  }

  async update(data: Partial<PuntoControl> & { id: string }): Promise<void> {
    const updatedData = {
      ...data,
      updated_at: this.getCurrentTimestamp()
    };
    await this.db.update('puntos_control', data.id, updatedData);
    this.emitSync('UPDATE', updatedData as any);
  }

  /**
   * Soft-delete: marca is_deleted=true (y sus eventos quedan huérfanos
   * lógicos: la UI los lista siempre desde el punto, así que desaparecen
   * con él; las filas quedan para auditoría, igual que el resto del mapa).
   */
  async delete(id: string): Promise<void> {
    await this.softDelete(id);
  }

  async getAll(): Promise<PuntoControl[]> {
    const rows = ((await this.db.getAll('puntos_control')) as any[]) || [];
    // Chequeo falsy a propósito (patrón ElementoMapaRepository): en Dexie
    // is_deleted puede llegar como 0/1 (hooks de normalización).
    return rows.filter(r => !r.is_deleted) as PuntoControl[];
  }

  async getByEmpresa(empresaId: string): Promise<PuntoControl[]> {
    const rows = await this.db.queryMultiple<PuntoControl>('puntos_control', [
      this.NOT_DELETED,
      { field: 'empresa_id', operator: 'equals', value: empresaId }
    ], { column: 'nombre', direction: 'asc' });
    return rows as PuntoControl[];
  }
}
